<template>
  <sp-menu
    class="finder-filter-menu"
    location="bottom-right"
    close-on-content-click="false"
    :label="t('filter_bar_menu_label')"
    :open.prop="open"
    :no-restore-focus="handoffPending"
    @update-open="onUpdateOpen"
    @close="onClosed"
  >
    <sp-button
      slot="activator"
      class="finder-filter-menu__trigger"
      :class="triggerModifiers"
      variant="text"
      color="custom"
      prepend-icon="settings"
      append-icon="popup-arrow-filled"
      :aria-expanded="open"
      :data-testid="TestId.finder.filterMenuTrigger"
    >
      {{ triggerLabel }}
    </sp-button>

    <div class="finder-filter-menu__panel" :data-testid="TestId.finder.filterMenuPanel">
      <header class="finder-filter-menu__header">
        <span class="finder-filter-menu__title">{{ t("filter_bar_menu_label") }}</span>
        <sp-button
          v-if="resettableCount > 0"
          class="finder-filter-menu__reset"
          size="small"
          variant="text-inline"
          color="danger"
          :title="t('filter_bar_menu_reset_title')"
          :data-testid="TestId.finder.filterMenuReset"
          @click="onClearAll"
        >
          {{ t("filter_bar_menu_reset") }}
        </sp-button>
      </header>

      <ul class="finder-filter-menu__list">
        <li v-for="menuItem in menuItems" :key="menuItem.id" class="finder-filter-menu__row">
          <sp-button
            v-if="canReveal(menuItem)"
            class="finder-filter-menu__row-label"
            :class="menuItemModifiers(menuItem)"
            variant="text"
            color="custom"
            :prepend-icon="menuItem.icon"
            :data-testid="TestId.finder.filterMenuRow"
            @click="onSelect(menuItem)"
          >
            {{ menuItem.label }}
          </sp-button>
          <sp-text v-else class="finder-filter-menu__row-label" :class="menuItemModifiers(menuItem)">
            <sp-icon v-if="menuItem.icon" :name="menuItem.icon"></sp-icon>
            {{ menuItem.label }}
          </sp-text>
          <sp-button
            v-if="isClearable(menuItem)"
            class="finder-filter-menu__row-clear"
            size="xx-small"
            color="danger"
            icon="close-bold"
            densitysmcompact="false"
            :label="clearLabel(menuItem)"
            :data-testid="TestId.finder.filterMenuRowClear"
            @click="onClear(menuItem)"
          ></sp-button>
        </li>
      </ul>
    </div>

    <sp-button
      slot="sheet-action"
      class="finder-filter-menu__done"
      color="primary"
      :data-testid="TestId.finder.filterMenuDone"
      @click="onDone"
    >
      {{ t("filter_bar_menu_done") }}
    </sp-button>
  </sp-menu>
</template>

<script setup>
/**
 * The bar's `Filters (n)` panel: an index of every filter the bar holds, which of them the visitor
 * has applied, and the controls to clear them. It owns the open state and pending focus handoff;
 * every row and number is derived from the bar's local filters and external contributions.
 */
import { computed, ref, shallowRef } from "vue";

import { TestId } from "#/test-ids";
import { t, tAccessibleName } from "#ce/shared/components-i18n";

const props = defineProps({
  /** @type {import("../composables/useFinderFilterMenu").FinderMenuItem[]} */
  menuItems: {
    type: Array,
    default: () => [],
  },
  /** How many filters the visitor has applied, clearable or not. */
  appliedCount: {
    type: Number,
    default: 0,
  },
  /**
   * How many of those Reset All can actually clear. A required filter counts as applied but cannot
   * be reset, so the control stays away rather than offering to do nothing.
   */
  resettableCount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(["select", "before-reveal", "clear", "clear-all"]);

const open = ref(false);
const pendingMenuItem = shallowRef();
const handoffPending = computed(() => Boolean(pendingMenuItem.value));

function canReveal(menuItem) {
  return Boolean(menuItem.actions.open || menuItem.actions.reveal);
}

function onDone() {
  open.value = false;
}

function onClosed() {
  const menuItem = pendingMenuItem.value;
  pendingMenuItem.value = undefined;
  if (menuItem) {
    emit("select", menuItem);
  }
}

const triggerLabel = computed(() =>
  props.appliedCount > 0 ? t("filter_bar_menu_label_count", { count: props.appliedCount }) : t("filter_bar_menu_label"),
);

const triggerModifiers = computed(() => {
  return { "--is-applied": props.appliedCount > 0 };
});

function menuItemModifiers(menuItem) {
  return { "--is-applied": isApplied(menuItem) };
}

function clearLabel(menuItem) {
  return tAccessibleName("filter_bar_menu_clear", { label: menuItem.label });
}

/** A row is bold once its filter holds a value, whether or not the visitor may clear it. */
function isApplied(menuItem) {
  return menuItem.state.active;
}

/** Only an applied filter the visitor may clear offers the control to do it. */
function isClearable(menuItem) {
  return menuItem.state.active && menuItem.state.clearable;
}

function onUpdateOpen(event) {
  const [value] = event.detail;
  open.value = value;
  if (value) {
    pendingMenuItem.value = undefined;
  }
}

function onSelect(menuItem) {
  if (menuItem.contributionId !== undefined) {
    pendingMenuItem.value = menuItem;
    emit("before-reveal");
    open.value = false;
    return;
  }
  open.value = false;
  emit("select", menuItem);
}

// The menu stays open so a second filter can be cleared without reopening it.
function onClear(menuItem) {
  emit("clear", menuItem);
}

function onClearAll() {
  emit("clear-all");
}
</script>

<style lang="scss" scoped>
/* The trigger reads as one of the bar's chips, so it takes the same
   `--sp-comp-filter-bar-activator-*` tokens the chips do and a client themes both at once. It wears
   the chip's applied chrome — surface and shadow — whether or not anything is applied. */
$menu-color: var(
  --sp-comp-finder-filter-bar-menu-color,
  var(--sp-comp-filter-bar-activator-label-color, var(--sp-sys-color-primary, #007078))
);
$menu-height: var(--sp-comp-filter-bar-activator-height, 4.375rem);
$menu-radius: var(--sp-comp-filter-bar-activator-border-radius, var(--sp-ref-border-radius-lg));
$menu-surface: var(--sp-comp-finder-filter-bar-menu-bg-color, var(--sp-sys-color-surface, #fff));

/* `sp-menu` is a full-width block, and the trigger is a control sized to its own label. */
.finder-filter-menu {
  /* `sp-popup` floors its own width, so the panel's width is set there rather than on the panel —
     a smaller value on the panel alone cannot reach below that floor. */
  --sp-comp-popup-min-width: var(--sp-comp-finder-filter-bar-menu-width, 17.5rem);

  width: auto;

  &__trigger {
    /* `sp-button` resolves a `text` variant's colour from its own `--sp-comp-button-<color>-*`
       family and pins the background to transparent, so the label is named through the custom
       family and the surface is painted here. */
    --sp-comp-button-custom-variant-text-text-color: #{$menu-color};
    --sp-comp-button-prepend-icon-fill: #{$menu-color};
    --sp-comp-button-append-icon-fill: #{$menu-color};
    --sp-comp-button-prepend-icon-size: var(--sp-ref-icon-size-x-small, 20px);
    --sp-comp-button-append-icon-size: var(--sp-ref-icon-size-xxx-small, 12px);
    --sp-comp-button-hover-bg-opacity: 0;
    --sp-comp-button-border-width: 1px;
    --sp-comp-button-border-style: solid;
    --sp-comp-button-border-color: #{$menu-surface};
    --sp-comp-button-border-radius: #{$menu-radius};
    --sp-comp-button-min-height: #{$menu-height};
    --sp-comp-button-size: #{$menu-height};
    --sp-comp-button-padding: 0 var(--sp-ref-scale-16);
    --sp-comp-button-gap: 0.25ch;
    --sp-comp-button-font-family: var(
      --sp-comp-filter-bar-activator-label-font-family,
      var(--sp-sys-font-family-semi-bold)
    );
    --sp-comp-button-font-size: var(
      --sp-comp-filter-bar-activator-label-font-size,
      var(--sp-sys-typescale-body-font-size)
    );
    --sp-comp-button-font-weight: var(
      --sp-comp-filter-bar-activator-label-font-weight,
      var(--sp-sys-typescale-body-semi-bold-font-weight, 600)
    );
    --sp-comp-button-text-transform: none;

    border-radius: #{$menu-radius};
    background: #{$menu-surface};
    box-shadow: var(--sp-comp-filter-bar-activator-shadow, var(--sp-sys-elevation-1, 0 1px 6px 0 rgb(0 0 0 / 25%)));
  }

  &__panel {
    padding-block: var(--sp-ref-scale-8);
  }

  &__header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--sp-ref-scale-8);
    padding-inline: var(--sp-ref-scale-16);
    padding-block-end: var(--sp-ref-scale-8);
  }

  &__title {
    font-family: var(--sp-sys-typescale-body-bold-font-family);
    font-weight: var(--sp-sys-typescale-body-bold-font-weight);
  }

  &__list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: var(--sp-ref-scale-4);
    padding-inline: var(--sp-ref-scale-8);
    border-block-end: 1px solid var(--sp-sys-color-outline-variant, #dde3e6);

    &:last-child {
      border-block-end: 0;
    }
  }

  &__row-label {
    --sp-comp-button-custom-variant-text-text-color: #{$menu-color};
    --sp-comp-button-prepend-icon-fill: #{$menu-color};
    --sp-comp-button-justify-content: start;

    flex: 1;

    &.--is-applied {
      --sp-comp-button-font-weight: var(--sp-sys-typescale-body-bold-font-weight, 700);
    }
  }

  &__done {
    --sp-comp-button-width: 100%;
  }
}
</style>
